import { Field, useFormikContext } from "formik";
import { FormValues } from "./utils/formSchema";
import { StyledTextField } from "./styled-components/AddressFields";
import { StyledEndAdorment } from "./AddressLookup";
import { Button, Grid, InputLabel, Stack, Typography } from "@mui/material";
import { useUserSlice } from "src/store/features/user.slice";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";

interface SsnEinModalFieldsProps {
  isLoading: boolean;
}

export default function SsnEinModalFields({ isLoading }: SsnEinModalFieldsProps) {
  const {
    values: { ssn, ein },
    errors: { ssn: ssnError, ein: einError },
    setFieldValue,
  } = useFormikContext<FormValues>();

  const {
    salesforceDetails: { ssnRequired, einRequired, recordType },
  } = useUserSlice();

  const handleChangeEin = (event: any) => {
    let einValue = event.target.value;
    einValue = einValue.replace(/[^\d-]/g, ""); //Remove non-digit and non-hyphen characters

    // Only insert the hyphen if it's not already there
    if (einValue.length > 2 && !einValue.includes("-")) {
      einValue = einValue.slice(0, 2) + "-" + einValue.slice(2);
    }
    setFieldValue("ein", einValue);
  };

  const handleChangeSsn = (event: any) => {
    let ssnValue = event.target.value;
    ssnValue = ssnValue.replace(/\D/g, ""); // Remove non-digit characters

    // Insert hyphens at the appropriate positions
    if (ssnValue.length > 3 && ssnValue.length <= 5) {
      ssnValue = ssnValue.slice(0, 3) + "-" + ssnValue.slice(3);
    } else if (ssnValue.length > 5) {
      ssnValue = ssnValue.slice(0, 3) + "-" + ssnValue.slice(3, 5) + "-" + ssnValue.slice(5, 9);
    }
    setFieldValue("ssn", ssnValue);
  };

  return (
    <>
      {/* Ssn Field */}
      {ssnRequired && recordType === "Individual" && (
        <Grid
          container
          style={{
            backgroundColor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 28,
          }}>
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "start" }}>
            <Typography fontSize={"17px"} sx={{ fontWeight: 400, textAlign: "center" }}>
              It looks like we are missing your Social Security Number (SSN)
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: "28px" }}>
            <Stack spacing={1}>
              <InputLabel sx={{ color: "#FC4C4C", fontSize: "16px", fontWeight: "600" }}>
                Social Security Number
              </InputLabel>
              <Field
                name="ssn"
                as={StyledTextField}
                type={"text"}
                placeholder="XXX-XX-XXXX"
                selected={ssn?.length === 11 && !ssnError}
                error={!!ssnError}
                helperText={
                  ssnError ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <ErrorOutlineRoundedIcon
                        sx={{ marginRight: "5px", color: "#EF4444", height: "20px", width: "20px" }}
                      />
                      {ssnError}
                    </div>
                  ) : null
                }
                sx={{
                  borderRadius: "16px",
                  "& .MuiFormHelperText-root": {
                    marginLeft: "0px",
                    color: "#EF4444",
                  },
                }}
                InputProps={{
                  endAdornment: <StyledEndAdorment selected={ssn?.length === 11 && !ssnError} />,
                  inputProps: { maxLength: 11 },
                }}
                onChange={handleChangeSsn}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: "28px", display: "flex", justifyContent: "center" }}>
            {/* Submit Button */}
            <Button
              disableRipple
              variant="contained"
              type="submit"
              disabled={!!ssnError || isLoading}
              style={{
                background: !!ssnError || isLoading ? "#D1D4D6" : "#FC4C4C",
                textTransform: "none",
                fontWeight: "600",
                width: "100%",
                fontSize: "18px",
                boxShadow: "none",
              }}>
              Submit
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Ein Field */}
      {einRequired && recordType === "Entity" && (
        <Grid
          container
          style={{
            backgroundColor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 28,
          }}>
          <Grid
            item
            xs={12}
            sx={{ display: "flex", justifyContent: "start", flexDirection: "column" }}>
            <Typography fontSize={"17px"} sx={{ fontWeight: 400, textAlign: "center" }}>
              The Employer Identification Number (EIN) for your entity is required and may not have
              been provided during onboarding. Please update it with the correct information.
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: "28px" }}>
            <Stack spacing={1}>
              <InputLabel sx={{ color: "#FC4C4C", fontSize: "16px", fontWeight: "600" }}>
                Employer Identification Number
              </InputLabel>
              <Field
                name="ein"
                as={StyledTextField}
                type={"text"}
                placeholder="XX-XXXXXXX"
                selected={ein?.length === 10 && !einError}
                error={!!einError}
                helperText={
                  einError ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <ErrorOutlineRoundedIcon
                        sx={{ marginRight: "5px", color: "#EF4444", height: "20px", width: "20px" }}
                      />
                      {einError}
                    </div>
                  ) : null
                }
                sx={{
                  borderRadius: "16px",
                }}
                InputProps={{
                  endAdornment: <StyledEndAdorment selected={ein?.length === 10 && !einError} />,
                  inputProps: { maxLength: 10 },
                }}
                onChange={handleChangeEin}
              />
            </Stack>
            <Stack>
              <Typography
                fontSize="14px"
                sx={{ fontWeight: 400, textAlign: "left", marginTop: "20px" }}>
                Note: if you are an exempt entity, such as a revocable living trust, please provide
                the Social Security Number of the grantor or responsible party.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: "28px", display: "flex", justifyContent: "center" }}>
            {/* Submit Button */}
            <Button
              disableRipple
              variant="contained"
              type="submit"
              disabled={!!einError || isLoading}
              style={{
                background: !!einError || isLoading ? "#D1D4D6" : "#FC4C4C",
                textTransform: "none",
                fontWeight: "600",
                width: "100%",
                fontSize: "18px",
                boxShadow: "none",
              }}>
              Submit
            </Button>
          </Grid>
        </Grid>
      )}
    </>
  );
}
